/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import api from '@/lib/api';
import {
  generateRsaKeyPair, exportPublicKeyB64, exportPrivateKeyEncrypted, importPrivateKeyEncrypted, importPublicKeyB64,
  hybridEncryptForRecipient, hybridDecryptWithPrivateKey, sha256Hex, base64ToBuf,
} from '@/lib/cryptoLocal';

/**
 * portal.js — client del Portale Paziente E2EE.
 *
 *  • Sessione portale: token JWT (scope `portal`, 15 min) ottenuto con OTP email,
 *    conservato in sessionStorage e inviato come header X-Portal-Token.
 *  • Chiave privata: DECIFRATA solo in memoria (module scope), mai persistita in chiaro.
 *  • Il server riceve esclusivamente blob cifrati.
 */

const TOKEN_KEY = 'sanitanexus.portal.token';
export const getPortalToken = () => sessionStorage.getItem(TOKEN_KEY);
export const setPortalToken = (t) => (t ? sessionStorage.setItem(TOKEN_KEY, t) : sessionStorage.removeItem(TOKEN_KEY));
const hdr = () => ({ headers: { 'X-Portal-Token': getPortalToken() || '' } });

// ---------------------------------------------------------------- sessione / OTP
export const portalSession = () => api.get('/portal/session', hdr()).then((r) => r.data);
export const portalOtpRequest = () => api.post('/portal/otp/request').then((r) => r.data);
export const portalOtpVerify = async (code) => {
  const r = await api.post('/portal/otp/verify', { code });
  setPortalToken(r.data.portal_token);
  return r.data;
};
export const portalLock = () => { setPortalToken(null); unlockedPrivateKey = null; };

// ---------------------------------------------------------------- chiavi paziente
let unlockedPrivateKey = null; // CryptoKey in memoria, mai serializzata
export const hasUnlockedKey = () => !!unlockedPrivateKey;

export const keyFingerprint = (publicKeyB64) => sha256Hex(new Uint8Array(base64ToBuf(publicKeyB64)));

/** Genera la coppia RSA nel browser e registra pub + priv cifrata (passphrase mai inviata). */
export const createPatientKeys = async (passphrase, hint) => {
  const pair = await generateRsaKeyPair();
  const public_key = await exportPublicKeyB64(pair.publicKey);
  const encrypted_private_key = await exportPrivateKeyEncrypted(pair.privateKey, passphrase);
  const fingerprint = await keyFingerprint(public_key);
  const r = await api.post('/portal/keys', { public_key, encrypted_private_key, fingerprint, hint: hint || null }, hdr());
  unlockedPrivateKey = pair.privateKey;
  return { ...r.data, fingerprint };
};

/** Scarica la chiave privata cifrata e la decifra localmente con la passphrase. */
export const unlockPatientKeys = async (passphrase) => {
  const { data } = await api.get('/portal/keys', hdr());
  unlockedPrivateKey = await importPrivateKeyEncrypted(data.encrypted_private_key, passphrase);
  return { fingerprint: data.fingerprint };
};

// ---------------------------------------------------------------- referti (paziente)
export const listMyReports = () => api.get('/portal/reports', hdr()).then((r) => r.data.items || []);

/** Scarica il blob cifrato e lo decifra in memoria → ArrayBuffer (PDF). */
export const decryptReport = async (reportId) => {
  if (!unlockedPrivateKey) throw new Error('Chiave privata bloccata: inserisci la passphrase');
  const { data } = await api.get(`/portal/reports/${reportId}`, hdr());
  const plain = await hybridDecryptWithPrivateKey(unlockedPrivateKey, data);
  const digest = await sha256Hex(plain);
  if (data.sha256 && digest !== data.sha256.toLowerCase()) throw new Error('Integrità non verificata: impronta SHA-256 diversa');
  api.post(`/portal/reports/${reportId}/ack`, {}, hdr()).catch(() => {});
  return { bytes: plain, meta: data, sha256: digest };
};

// ---------------------------------------------------------------- lato professionista
export const searchPortalPatients = (q) => api.get('/portal/patients/search', { params: { q } }).then((r) => r.data.items || []);
export const getPatientPublicKey = (patientId) => api.get(`/portal/public-key/${patientId}`).then((r) => r.data);
export const listSentReports = () => api.get('/portal/reports/sent').then((r) => r.data.items || []);
export const revokeReport = (id) => api.delete(`/portal/reports/${id}`).then((r) => r.data);
/** Workflow di approvazione: stato → "approved" e notifica GDPR-safe in background (email + magic link 15' + in-app). */
export const approveReport = (id) => api.post(`/portal/reports/${id}/approve`).then((r) => r.data);

/** Cifra il file nel browser con la chiave pubblica del paziente e lo invia. */
export const sendEncryptedReport = async ({ patient, file, title, category, note, approve = false }) => {
  const pub = await getPatientPublicKey(patient.id);
  await importPublicKeyB64(pub.public_key); // valida la chiave
  const bytes = await file.arrayBuffer();
  const payload = await hybridEncryptForRecipient(pub.public_key, bytes);
  const body = {
    patient_id: patient.id, title, category: category || 'referto', filename: file.name, mime: file.type || 'application/pdf', size: bytes.byteLength,
    sha256: payload.sha256, alg: payload.alg, iv: payload.iv, ciphertext: payload.ciphertext, wrapped_key: payload.wrapped_key,
    recipient_fingerprint: pub.fingerprint, note: note || null, approve: !!approve,
  };
  return api.post('/portal/reports', body).then((r) => r.data);
};
